import { isPointInsidePolygon } from '@utils/fabric-util'
/**
 * 转换命令数据为点数组并去重
 * @param {Array} data - 图形命令数据
 * @returns {Array} points - 点数组 [{x, y}]
 */
export function convertPoints(data) {
  const points = data
    .filter(item => item.length === 3 && (item[0] === 'M' || item[0] === 'L')) // 只保留 M 和 L 指令
    .map(item => ({ x: item[1], y: item[2] })) // 转换为 { x, y } 格式

  // 去重，使用字符串表示点
  const uniquePoints = Array.from(new Set(points.map(p => `${p.x},${p.y}`))).map(str => {
    const [x, y] = str.split(',').map(Number)
    return { x, y }
  })
  return uniquePoints
}

// 判断是否是不规则多边形
export function isSpecialRectangle(points) {
  // 1. 确保是四边形
  if (points.length !== 4) {
    return false
  }

  let horizontalEdges = 0
  let verticalEdges = 0

  // 2. 计算每条边的斜率
  for (let i = 0; i < 4; i++) {
    const p1 = points[i]
    const p2 = points[(i + 1) % 4] // 环形连接最后一个点与第一个点
    const dx = p2.x - p1.x
    const dy = p2.y - p1.y

    if (dy === 0) {
      // 水平边，斜率为 0
      horizontalEdges++
    } else if (dx === 0) {
      // 垂直边，斜率为无穷大
      verticalEdges++
    } else {
      // 其他边，不符合规则
      return false
    }
  }

  // 3. 确保有两条水平边和两条垂直边
  return horizontalEdges === 2 && verticalEdges === 2
}

export function findTopLeft(points) {
  // 找到 y 最小值
  const minY = Math.min(...points.map(point => point.y))

  // 筛选所有 y 等于 minY 的点
  const minYPoints = points.filter(point => point.y === minY)

  // 在这些点中找到 x 最小的点
  const topLeft = minYPoints.reduce((acc, point) => (point.x < acc.x ? point : acc), minYPoints[0])

  return topLeft
}

/**
 * 提取图形的边
 * @param {Array} points - 点数组 [{x, y}]
 * @returns {Array} edges - 边数组，每条边是两个点 [{x, y}, {x, y}]
 */
function extractEdges(points) {
  const edges = []
  for (let i = 0; i < points.length; i++) {
    const startPoint = points[i]
    const endPoint = points[(i + 1) % points.length] // 闭合多边形
    edges.push([startPoint, endPoint])
  }
  return edges
}

/**
 * 找到正斜率最小的边
 * @param {Array} edges - 边数组，每条边是两个点 [{x, y}, {x, y}]
 * @returns {Array|null} 最佳边或 null
 */
function findMinPositiveSlopeEdge(edges, room) {
  let candidates = []
  let bestEdge = null

  // 1. 计算多边形的中心点（或者重心）
  let sumY = 0
  let numPoints = 0
  for (const [p1, p2] of edges) {
    sumY += p1.y + p2.y
    numPoints += 2 // 每条边有两个点
  }

  const centerY = sumY / numPoints // 多边形的重心的 y 坐标

  // 2. 优先寻找接近水平的线段（斜率的绝对值在 0 和 10 之间）
  for (const [p1, p2] of edges) {
    const dx = p2.x - p1.x
    const dy = p2.y - p1.y

    const slope = dy / dx
    const absSlope = Math.abs(slope)

    // 如果斜率的绝对值在 0 和 10 之间，认为是近似水平线
    if (absSlope >= 0 && absSlope <= 0.3) {
      candidates.push({ p1, p2, slope, absSlope, minY: Math.min(p1.y, p2.y) })
    }
  }

  if (candidates.length > 0) {
    candidates.sort((a, b) => {
      if (a.minY !== b.minY) {
        return a.minY - b.minY // 按 minY 升序排列
      }
      return Math.abs(a.slope) - Math.abs(b.slope) // 如果 minY 相同，按斜率降序排列，最小斜率优先
    })
    // candidates.sort((a, b) => Math.abs(a.slope) - Math.abs(b.slope))

    bestEdge = [candidates[0].p1, candidates[0].p2]
  } else {
    // 如果找不到近似水平的线段，使用现有逻辑
    for (const [p1, p2] of edges) {
      const dx = p2.x - p1.x
      const dy = p2.y - p1.y

      const slope = dy / dx

      // 仅考虑正斜率和负斜率的线段
      if (p1.y < centerY || p2.y < centerY) {
        candidates.push({ p1, p2, slope, minY: Math.min(p1.y, p2.y) })
      }
    }

    if (candidates.length === 0) {
      return null // 如果没有符合条件的线段，返回 null
    }

    // 按斜率从大到小排序
    candidates.sort((a, b) => a.slope - b.slope)

    bestEdge = [candidates[0].p1, candidates[0].p2]
  }

  // 6. 找到 x 坐标最小的点
  let minXPoint = bestEdge[0].x < bestEdge[1].x ? bestEdge[0] : bestEdge[1]

  let x, y

  // 判断是否在多边形内
  if (!isPointInsidePolygon(minXPoint, room)) {
    x = minXPoint.x
    y = minXPoint.y - 12 - 18
  } else {
    x = minXPoint.x
    y = minXPoint.y
  }

  return { x, y }
}

// Helper function: 判断点是否在多边形内（射线法）
function isPointInPolygon(point, polygon) {
  const { x, y } = point;
  let isInside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const { x: xi, y: yi } = polygon[i];
      const { x: xj, y: yj } = polygon[j];
      const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) isInside = !isInside;
  }
  return isInside;
}

// Helper function: 投影点到多边形内部（简单方法）
function projectPointToPolygon(point, polygon) {
  let minDistance = Infinity;
  let closestPoint = point;

  for (let i = 0; i < polygon.length; i++) {
      const p1 = polygon[i];
      const p2 = polygon[(i + 1) % polygon.length];
      const projected = projectPointToSegment(point, p1, p2);
      const distance = getDistance(point, projected);
      if (distance < minDistance) {
          minDistance = distance;
          closestPoint = projected;
      }
  }

  return closestPoint;
}

// Helper function: 投影点到线段上
function projectPointToSegment(point, p1, p2) {
  const { x, y } = point;
  const { x: x1, y: y1 } = p1;
  const { x: x2, y: y2 } = p2;

  const dx = x2 - x1;
  const dy = y2 - y1;
  if (dx === 0 && dy === 0) return p1;

  const t = ((x - x1) * dx + (y - y1) * dy) / (dx * dx + dy * dy);
  const clampedT = Math.max(0, Math.min(1, t));

  return {
      x: x1 + clampedT * dx,
      y: y1 + clampedT * dy,
  };
}

// Helper function: 计算两点距离
function getDistance(p1, p2) {
  return Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2);
}

// Modified Centroid Calculation Function
export function calculateCentroid(vertices) {
  // 原几何质心计算
  let area = 0;
  let Cx = 0;
  let Cy = 0;
  const n = vertices.length;

  for (let i = 0; i < n; i++) {
      const { x: xi, y: yi } = vertices[i];
      const { x: xj, y: yj } = vertices[(i + 1) % n];

      const cross = xi * yj - xj * yi;
      area += cross;
      Cx += (xi + xj) * cross;
      Cy += (yi + yj) * cross;
  }

  area /= 2;
  if (area === 0) {
      throw new Error("多边形面积为零，无法计算质心。");
  }

  Cx /= 6 * area;
  Cy /= 6 * area;

  const centroid = { x: Cx, y: Cy };

  // 如果质心在多边形外，修正到内部
  if (!isPointInPolygon(centroid, vertices)) {
      return projectPointToPolygon(centroid, vertices);
  }

  return centroid;
}

export function calculateVertexCentroid(vertices) {
  const n = vertices.length;
  let Cx = 0;
  let Cy = 0;

  vertices.forEach(({ x, y }) => {
      Cx += x;
      Cy += y;
  });

  return { x: Cx / n, y: Cy / n };
}

function calculateCentroidBySampling(vertices, sampleCount = 10000) {
    // 获取多边形边界框
    const bounds = vertices.reduce(
        (acc, { x, y }) => ({
            minX: Math.min(acc.minX, x),
            minY: Math.min(acc.minY, y),
            maxX: Math.max(acc.maxX, x),
            maxY: Math.max(acc.maxY, y),
        }),
        { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity }
    );

    const points = [];
    for (let i = 0; i < sampleCount; i++) {
        const x = Math.random() * (bounds.maxX - bounds.minX) + bounds.minX;
        const y = Math.random() * (bounds.maxY - bounds.minY) + bounds.minY;

        if (isPointInPolygon({ x, y }, vertices)) {
            points.push({ x, y });
        }
    }

    if (points.length === 0) {
        throw new Error("未能生成多边形内部的随机点。");
    }

    // 计算采样点的平均坐标
    const Cx = points.reduce((sum, p) => sum + p.x, 0) / points.length;
    const Cy = points.reduce((sum, p) => sum + p.y, 0) / points.length;

    return { x: Cx, y: Cy };
}




// 主函数：整合处理流程
export function processAndFindBestEdge(data, room) {
  const points = convertPoints(data) // 转换点数据并去重
  const edges = extractEdges(points) // 提取边
  return findMinPositiveSlopeEdge(edges, room) // 找到斜率最小的边
}
